import _ from 'lodash';
import moment from 'moment';

const USER_FRIENDLY_FORMAT = 'h:mm a';
const LAST_FLIGHT = 'lastFlight';
const LAST_FLIGHT_FORMATS = ['lastflight', 'last', 'last flight', 'lf'];

export const MON = { label: 'MON', value: 'Mo' };
export const TUE = { label: 'TUE', value: 'Tu' };
export const WED = { label: 'WED', value: 'We' };
export const THU = { label: 'THU', value: 'Th' };
export const FRI = { label: 'FRI', value: 'Fr' };
export const SAT = { label: 'SAT', value: 'Sa' };
export const SUN = { label: 'SUN', value: 'Su' };

export const DAYS = [MON, TUE, WED, THU, FRI, SAT, SUN];

const continuousBucketToString = daysArray => {
    if (daysArray.length === 0) {
        return '';
    }

    if (daysArray.length === 1) {
        return daysArray[0].value;
    }

    return `${_.first(daysArray).value}-${_.last(daysArray).value}`;
};

const sort = daysArray => _.sortBy(daysArray, day => _.indexOf(DAYS, day));

export const daysToBuckets = (daysArray = []) => {
    const buckets = [];
    _.forEach(sort(daysArray), day => {
        const dayIndex = _.indexOf(DAYS, day);
        // check if last item in buckets array has a last item that's the day before day
        const possibleBucket = _.last(buckets);
        const possiblePreviousDay = _.last(possibleBucket);
        if (possiblePreviousDay && _.indexOf(DAYS, possiblePreviousDay) + 1 === dayIndex) {
            possibleBucket.push(day);
        } else {
            buckets.push([day]);
        }
    });
    return buckets;
};

export const daysToString = (daysArray = []) => {
    const buckets = daysToBuckets(daysArray);
    return _.join(buckets.map(continuousBucketToString), ',');
};

const MOMENT_FORMATS = ['h:mm a', 'h:mma', 'ha', 'h a', 'H', 'H:mm'];
const IMPLICIT_AM_PM_FORMATS = ['h', 'h:mm'];

const isLastFlightString = (input, isOpens) => !isOpens && _.includes(LAST_FLIGHT_FORMATS, _.toLower(input));

export const validateTimeString = (input, isOpens) => {
    let valid = false;

    if (!isOpens && isLastFlightString(input, isOpens)) {
        return true;
    }

    const isValidIncrement = () => {
        const convertedMoment = convertInputToMoment(input, isOpens);

        // Convert time to minutes and then check for 15 min increment
        return moment.duration(convertedMoment.format('HH:mm')).asMinutes() % 15 === 0;
    };

    _.forEach(MOMENT_FORMATS, format => {
        if (moment(input, format, true).isValid() && isValidIncrement()) {
            valid = true;
            return false; // early exit, don't need to check other formats
        }
    });

    return valid;
};

export const convertInputToMoment = (input, isOpens) => {
    let shouldAppendPM = false;
    if (!isOpens) {
        _.forEach(IMPLICIT_AM_PM_FORMATS, format => {
            if (moment(input, format, true).isValid()) {
                shouldAppendPM = true;
                return false;
            }
        });
    }

    const modifiedInput = `${input}${shouldAppendPM ? 'pm' : ''}`;

    let result = null;
    _.forEach(MOMENT_FORMATS, format => {
        const m = moment(modifiedInput, format, true);
        if (m.isValid()) {
            // const formatter = openingTime ? 'HH:mm' : 'kk:mm'
            // result = m.format(formatter)

            result = m;
            return false; // early exit, don't need to check other formats
        }
    });
    return result;
};

export const toUserFriendlyTimeString = (input, isOpens) => {
    if (isLastFlightString(input, isOpens)) {
        return LAST_FLIGHT;
    }

    const m = convertInputToMoment(input, isOpens);
    return m ? m.format(USER_FRIENDLY_FORMAT) : input;
};

//
// export const userInputToOSMString = (daysArray, userFriendlyOpen, userFriendlyClose) => {
//   const days = daysToString(daysArray)
//   const startMoment = moment(userFriendlyOpen, USER_FRIENDLY_FORMAT)
//   const endMoment = moment(userFriendlyClose, USER_FRIENDLY_FORMAT)
//
//   if (!(startMoment.isValid() && endMoment.isValid())) {
//     // we shouldn't get here
//     return 'invalid'
//   }
//
//   const start = startMoment.isValid() ? startMoment.format('HH:mm') : 'invalid'
//   const end = endMoment.isValid() ? endMoment.format('kk:mm') : 'invalid'
//
//   return `${days} ${start}-${end}`
// }

export const userInputToSimplifiedOSMStrings = (daysArray, userFriendlyOpen, userFriendlyClose) => {
    const result = [];
    const buckets = daysToBuckets(daysArray);
    const closeTimeIsLastFlight = userFriendlyClose === LAST_FLIGHT;

    const startMoment = moment(userFriendlyOpen, USER_FRIENDLY_FORMAT);
    const endMoment = closeTimeIsLastFlight ? moment() : moment(userFriendlyClose, USER_FRIENDLY_FORMAT);
    //
    // if (!(startMoment.isValid() && endMoment.isValid())) {
    //   // we shouldn't get here
    //   result.push('invalid')
    //   return
    // }

    const start = startMoment.isValid() ? startMoment.format('HH:mm') : 'invalid';
    let end = LAST_FLIGHT;
    if (!closeTimeIsLastFlight) {
        end = endMoment.isValid() ? endMoment.format('kk:mm') : 'invalid';
    }

    _.map(buckets, bucket => {
        const days = daysToString(bucket);
        result.push(`${days} ${start}-${end}`);
    });

    return result;
};

export const osmDaysToPrettyDays = daysString => {
    const replacements = _.map(DAYS, day => ({
        re: new RegExp(day.value, 'g'),
        pretty: day.label,
    }));

    replacements.push({ re: /,/g, pretty: ', ' });
    replacements.push({ re: /-/g, pretty: ' \u2013 ' });

    let result = daysString;
    _.forEach(replacements, replacementConfig => {
        result = _.replace(result, replacementConfig.re, replacementConfig.pretty);
    });

    return result;
};

export const osmHoursToPrettyHours = hoursString => {
    const hoursParts = _.split(hoursString, '-');
    if (hoursParts.length !== 2) {
        return hoursString;
    }
    const prettyBegin = _.toUpper(moment(hoursParts[0], 'HH:mm').format('h:mm a'));
    const prettyEnd =
        hoursParts[1] === LAST_FLIGHT ? 'Last Flight' : _.toUpper(moment(hoursParts[1], 'kk:mm').format('h:mm a'));

    return `${prettyBegin} \u2013 ${prettyEnd}`;
};
